import styles from './control-bar.module.scss';
import { Button, Progress, Select } from 'antd';
import type { MediaDevice } from '../../video-types';
import { IconFont } from '../../../../component/icon-font';
import AudioIcon from '../../../../component/svgs/audio/audio-unmute-white.svg';
import CameraIcon from '../../../../component/svgs/video/video-unmuted.svg';

interface ControlBarProps {
  isPlayingAudio: boolean;
  inputLevel: number;
  speakerList: MediaDevice[];
  activeSpeaker: string;
  outputLevel: number;
  microphoneBtn: string;
  activeMicrophone: string;
  micList: MediaDevice[];
  onSwitchSpeaker: (key: string) => void;
  onSwitchMicrophone: (key: string) => void;
  onTestMicrophoneClick: () => void;
  onTestSpeakerClick: () => void;
}

const { Option } = Select;

const BAR_COUNT = 5;
// Volume thresholds (0–100) for each bar to light up — rightmost bars need higher volume
const BAR_THRESHOLDS = [10, 30, 50, 70, 90];

// Data for each bar taken directly from SVG design (viewBox 36×22)
// x=position, y=top, h=height — bars grow from center vertically
const BAR_DEFS = [
  { x: 0, y: 4.319, h: 10.588 }, // bar 1 — lowest
  { x: 8, y: 1.2, h: 18.825 }, // bar 2
  { x: 16, y: 1.2, h: 18.825 }, // bar 3
  { x: 24, y: 0, h: 21.225 }, // bar 4 — highest
  { x: 32, y: 0, h: 21.225 } // bar 5 — highest
] as const;

const COLOR_LIT = '#7555F5';
const COLOR_DIM = 'rgba(241, 237, 254, 0.2)';

const MicVolumeBars = ({ volume }: { volume: number }) => {
  return (
    <svg
      className="mic-volume-bars"
      width="36"
      height="22"
      viewBox="0 0 36 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {BAR_DEFS.map((bar, i) => {
        const lit = volume >= BAR_THRESHOLDS[i];
        return (
          <rect
            key={i}
            x={bar.x}
            y={bar.y}
            width="4"
            height={bar.h}
            rx="2"
            fill={lit ? COLOR_LIT : COLOR_DIM}
            style={{ transition: 'fill 0.08s ease' }}
          />
        );
      })}
    </svg>
  );
};

const ControlBar = (props: ControlBarProps) => {
  const {
    isPlayingAudio,
    inputLevel,
    speakerList,
    activeSpeaker,
    outputLevel,
    microphoneBtn,
    activeMicrophone,
    micList,
    onSwitchSpeaker,
    onSwitchMicrophone,
    onTestMicrophoneClick,
    onTestSpeakerClick
  } = props;

  return (
    <div className={styles.width100}>
      <div className={styles.container}>
        <div className={styles.itemContainer}>
          <div className={styles.iconContainer}>
            <img
              src={AudioIcon}
              alt="magic"
              style={{
                width: 22,
                height: 22
              }}
            />
          </div>
          <div className={styles.optionContainer}>
            <div className={styles.title}>Microphone</div>

            <div>
              <select
                className={styles.select}
                value={activeSpeaker}
                onChange={(e) => {
                  onSwitchMicrophone(e.target.value);
                }}
              >
                {micList.map((item) => (
                  <option value={item.deviceId} key={item.deviceId}>
                    {item.label}
                  </option>
                ))}
              </select>
              <button className={styles.textBtn} onClick={onTestMicrophoneClick}>
                {microphoneBtn}
              </button>
            </div>
          </div>
          <div className={styles.micVolume}>
            <MicVolumeBars volume={inputLevel} />
          </div>
        </div>
        <div className={styles.divider} />
        <div className={styles.itemContainer}>
          <div className={styles.iconContainer}>
            <img
              src={CameraIcon}
              alt="magic"
              style={{
                width: 22,
                height: 22
              }}
            />
          </div>
          <div className={styles.optionContainer}>
            <div className={styles.title}>Speaker</div>
            <div>
              <select
                className={styles.select}
                value={activeSpeaker}
                onChange={(e) => {
                  onSwitchSpeaker(e.target.value);
                }}
              >
                {speakerList.map((item) => (
                  <option value={item.deviceId} key={item.deviceId}>
                    {item.label}
                  </option>
                ))}
              </select>
              <button className={styles.textBtn} onClick={onTestSpeakerClick}>
                {isPlayingAudio ? 'Stop' : 'Test'}
              </button>
            </div>
          </div>
          <div className={styles.progress}>
            <Progress percent={outputLevel} showInfo={false} trailColor="#F1EDFE33" strokeColor="#7555F5" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ControlBar;
